import React from 'react';
import { ReactComponent as Datepicker } from 'assets/icons/calendar.svg';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import { twJoin } from 'tailwind-merge';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import FieldError from './FieldError';
import FieldLabel from './FieldLabel';
import FieldDescribe from './FieldDescribe';
import 'react-datepicker/dist/react-datepicker.css';
import '../../react-datapicker/index.css';

type ControlledDatepickerProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  label: string | React.ReactNode;
  classWrapperName?: string;
  describe?: React.ReactNode;
  disabled?: boolean;
  isRequired?: boolean;
  withoutTouch?: boolean;
} & DatePickerProps;

const renderDayContents = (day: number) => {
  return <span className="flex items-center justify-center h-full">{day}</span>;
};

const FormDatepicker = <TFormValues extends FieldValues>({
  name,
  label,
  className,
  classWrapperName,
  isRequired,
  disabled,
  describe,
  withoutTouch = true,
  ...props
}: ControlledDatepickerProps<TFormValues>) => {
  const { control } = useFormContext<TFormValues>();
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({ name, control });

  const touch = withoutTouch || isTouched;

  return (
    <div
      className={twJoin(
        'flex flex-col gap-0.5',
        classWrapperName,
        disabled && 'opacity-50',
      )}
    >
      <FieldLabel name={name} label={label} isRequired={isRequired} />

      <DatePicker
        id={name}
        selected={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        showPopperArrow={false}
        icon={<Datepicker className="top-1/2 -translate-y-1/2 mr-2" />}
        renderDayContents={renderDayContents}
        popperPlacement="bottom"
        popperClassName="-mt-3"
        className={twJoin(
          className,
          'border border-med-gray-3 text-sm h-12 w-full placeholder-med-gray-3 placeholder:font-medium outline-none focus:border-blue-600 rounded-5 py-1.5 px-3',
          error && touch && 'border-alert-negative bg-error-yellow',
        )}
        disabled={disabled}
        showIcon
        {...props}
      />

      {error && touch && (
        <FieldError className="-mt-1.5">{error?.message as string}</FieldError>
      )}
      {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
    </div>
  );
};

export default FormDatepicker;
